// verts_t - verts_0 -> the head trunk's conditioning input.
//
// A head trained with --head_mesh_cond (TexAvatars) takes NO rig vector: its
// trunk is exp2code(disp_encoder(verts_t - verts_0)), so the DISPLACEMENT from
// the rest pose is the whole conditioning signal. That suits this repo's ORL
// path exactly -- ORL already produces posed vertices, so the same array that
// feeds the lifter conditions the decoders, and no per-character control
// mapping has to agree with the trained rig space.
//
// WHY THE POOLED ENCODER WAS NOT USED (and why this must stay a raw field):
// the trained projection reads the displacement LINEARLY. A pooled
// (PointNet-style) encoder max-pools over the vertex axis and that max sits on
// extreme points -- nose tip, ear, chin outline -- which barely move between
// expressions; measured over 49 head poses it produced one constant code
// (separation ratio 0.0055 against a 0.02 floor). So nothing here may reduce,
// pool or resample the field.

/** Capture the rest pose the displacement is measured against. Copies: the
 *  caller's array is the live posed buffer and will be written in place. */
export function captureRestPose(verts) {
  return new Float32Array(verts)
}

/** disp = verts - rest, written into `out` (allocated on first call, reused
 *  after). Returns null when the inputs disagree, so a caller cannot feed a
 *  half-filled buffer to the trunk. */
export function computeDisplacement(verts, rest, out) {
  if (!verts || !rest || verts.length !== rest.length) return null
  const dst = out && out.length === verts.length ? out : new Float32Array(verts.length)
  for (let i = 0; i < verts.length; i++) dst[i] = verts[i] - rest[i]
  return dst
}

/** Largest |component| of a displacement field -- the cheap "did the mesh
 *  actually move" probe.
 *
 *  THIS IS NOT COSMETIC. On the GPU deform path the posed vertices go straight
 *  to the lifter as a GPUBuffer and the CPU `branch.verts` array STAYS AT ITS
 *  NEUTRAL POSE, so a displacement computed from it is exactly zero -- a face
 *  frozen at rest, with the graph running perfectly well and no error anywhere.
 *  Callers use this to refuse that case loudly instead of shipping it. */
export function maxAbs(disp) {
  let m = 0
  for (let i = 0; i < disp.length; i++) {
    const a = disp[i] < 0 ? -disp[i] : disp[i]
    if (a > m) m = a
  }
  return m
}
